import { HttpClient } from './HttpClient';
import { getAuthRoot, getViewerRoot, tryGetPacsRoot } from '../config/roots';
import { readCookie } from '../utils';

export { HttpClient } from './HttpClient';
export { HttpException, UnauthorizedException } from './exceptions';
export type { RequestOptions, HttpResponse, HttpClientProviders } from './HttpClient';

/**
 * D6. Set once from preRegistration, read per 401 — the same providers-not-values shape
 * as getBaseUrl, and for the same reason: viewerHttp is constructed at module scope,
 * long before any service exists to hand it a handler.
 */
let onViewerUnauthorized: (() => void) | null = null;

export function setViewerUnauthorizedHandler(handler: () => void): void {
  onViewerUnauthorized = handler;
}

/**
 * The environment-management transport. viewerHttp arrives at M3, orthancHttp at M4.
 *
 * Because HttpClient takes providers, these shared instances are a CONVENIENCE
 * for consistent auth and error handling, not a correctness requirement — the
 * inverse of develop, where `new HttpClient(getViewerUrl())` in six places is a
 * bug six times over (§5.6).
 */
export const authHttp = new HttpClient({
  getBaseUrl: getAuthRoot,
  getAuthHeader: () => {
    const accessToken = readCookie('accessToken');
    // No cookie -> no header. DicomWebDataSource guards on
    // `authHeaders.Authorization` being present (index.ts:210-217), and an absent
    // Authorization header also keeps local cross-origin requests simple rather
    // than preflighted.
    return accessToken ? { Authorization: `Bearer ${accessToken}` } : undefined;
  },
});

/**
 * The study-management transport (§5.1). Constructed at module scope, which is safe
 * only because HttpClient takes PROVIDERS: getViewerRoot() is invoked per request,
 * not here. It calls fatal() when the viewerUrl cookie is absent — which is every
 * local boot without /dev/login — so had M2 taken base URLs by value, importing this
 * module would take the extension down at boot and with it every sycai toolbar
 * button (§10 risk 3).
 *
 * getAuthHeader is byte-identical to authHttp's, and deliberately trivial: M2's debt
 * register notes that a provider doing real work would be laundered into
 * HttpException(0), because getAuthHeader is invoked inside HttpClient's try at :100
 * while buildUrl was deliberately hoisted out of it. The day this provider does more
 * is the day to hoist it too.
 */
export const viewerHttp = new HttpClient({
  getBaseUrl: getViewerRoot,
  getAuthHeader: () => {
    const accessToken = readCookie('accessToken');
    return accessToken ? { Authorization: `Bearer ${accessToken}` } : undefined;
  },
  onUnauthorized: () => onViewerUnauthorized?.(),
});

/**
 * The PACS transport (§5.1), and the OTHER root -- M4.1 §5.1 documented two and declared
 * this one out of scope. tryGetPacsRoot() reads wadoRoot off the ACTIVE DATA SOURCE and
 * strips /dicom-web, never off the cookie (M2 D4), and it is invoked PER REQUEST: it is
 * callable only after route entry, because getConfig() returns a copy assigned inside
 * initialize() (DicomWebDataSource/index.ts:779-781, :208). Constructing this at module
 * scope is safe for exactly the reason viewerHttp's is -- HttpClient takes providers, not
 * values.
 *
 * `tryGetPacsRoot`, NOT `getPacsRoot` (M4.2 whole-branch review, Important 2). Every other
 * root getter in this file is on the boot path and calls `fatal()` on failure, which is
 * correct there (§5.4). This one is not: `QACheckItem` is a passive per-series readout
 * mounted on every CT viewport, and `sycai-local.js`'s `dicomjson`/`dicomlocal` data
 * sources report no `wadoRoot` at all, so `getPacsRoot()` would call `fatal()` -- which
 * raises `globalThis.reportError` BEFORE throwing (utils/fatal.ts) -- on every mount under
 * `/local`, uncaught, with nothing cached to stop the retry. `tryGetPacsRoot` returns
 * `undefined` instead, which routes through `HttpClient.buildUrl`'s existing empty-baseUrl
 * guard: a plain `Error`, caught by `fetchLabels` like any other transport failure. See
 * config/roots.ts's `tryGetPacsRoot` docblock for the full trace.
 *
 * D-3: NO onUnauthorized, unlike viewerHttp. A passive readout must not be able to end a
 * session, and the DICOMweb data source -- same host, same bearer -- does not either
 * (DicomWebDataSource/index.ts:210-217 merely omits the header when there is none). A 401
 * here is logged by the caller and nothing is cached, so the next mount retries. The
 * non-fatal `getBaseUrl` above is the same D-3 principle applied to a config failure
 * instead of a transport one: neither may escalate past what a passive readout can afford.
 */
export const orthancHttp = new HttpClient({
  getBaseUrl: tryGetPacsRoot,
  getAuthHeader: () => {
    const accessToken = readCookie('accessToken');
    return accessToken ? { Authorization: `Bearer ${accessToken}` } : undefined;
  },
});
